import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { getApiBaseUrl } from '../utils/getApiUrl';
import { canAccessComplaints, isMainAdmin } from '../utils/complaintsAccess';
import ConfirmModal from '../components/common/ConfirmModal';
import { SURVEY_QUESTION_LABELS, SURVEY_QUESTIONS } from '../constants/surveyQuestions';

type ComplaintStatus = 'submitted' | 'under_review' | 'closed';

interface ComplaintNote {
  id: number;
  admin_username: string;
  notes: string;
  created_at: string;
}

interface RelatedAppointment {
  id: number;
  tracking_code?: string;
  status: string;
  created_at: string;
  service_name?: string;
}

interface AppointmentDetailModal {
  id: number;
  tracking_code?: string;
  status: string;
  created_at: string;
  appointment_date?: string;
  appointment_time?: string;
  cancel_reason?: string;
  user?: { full_name?: string; phone?: string; national_id?: string };
  service?: { name?: string };
  services?: { name: string }[];
}

interface ComplaintDetail {
  id: number;
  user_id?: number;
  full_name: string;
  phone: string;
  description: string;
  submission_type?: 'complaint' | 'survey';
  survey_data?: Record<string, string>;
  tracking_code?: string;
  status: ComplaintStatus;
  resolution_summary?: string;
  assigned_admin_username?: string;
  created_at: string;
  notes: ComplaintNote[];
}

const STATUS_OPTIONS: { value: ComplaintStatus; label: string }[] = [
  { value: 'submitted', label: 'ثبت شده' },
  { value: 'under_review', label: 'در حال بررسی' },
  { value: 'closed', label: 'بسته شده' },
];

const STATUS_STYLES: Record<
  ComplaintStatus,
  { badge: string; ring: string; dot: string }
> = {
  submitted: {
    badge: 'bg-amber-50 text-amber-800 border-amber-200',
    ring: 'ring-amber-200',
    dot: 'bg-amber-500',
  },
  under_review: {
    badge: 'bg-blue-50 text-blue-800 border-blue-200',
    ring: 'ring-blue-200',
    dot: 'bg-blue-500',
  },
  closed: {
    badge: 'bg-slate-100 text-slate-700 border-slate-200',
    ring: 'ring-slate-200',
    dot: 'bg-slate-400',
  },
};

const HEADER_STATUS_STYLES: Record<ComplaintStatus, { badge: string; dot: string }> = {
  submitted: { badge: 'bg-amber-500/20 text-amber-100 border-amber-400/40', dot: 'bg-amber-400' },
  under_review: { badge: 'bg-blue-500/20 text-blue-100 border-blue-400/40', dot: 'bg-blue-400' },
  closed: { badge: 'bg-slate-500/30 text-slate-200 border-slate-400/40', dot: 'bg-slate-300' },
};

const getStatusLabel = (s: ComplaintStatus) =>
  STATUS_OPTIONS.find((o) => o.value === s)?.label ?? s;

const normalizeComplaintStatus = (value: unknown): ComplaintStatus => {
  const raw = String(value ?? 'submitted');
  if (raw === 'resolved') return 'closed';
  if (raw === 'submitted' || raw === 'under_review' || raw === 'closed') return raw;
  return 'submitted';
};

const AdminComplaintDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [complaint, setComplaint] = useState<ComplaintDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<ComplaintStatus>('submitted');
  const [resolutionSummary, setResolutionSummary] = useState('');
  const [newNote, setNewNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [resolutionLocked, setResolutionLocked] = useState(false);
  const [relatedAppointments, setRelatedAppointments] = useState<RelatedAppointment[]>([]);
  const [appointmentModalOpen, setAppointmentModalOpen] = useState(false);
  const [appointmentModalLoading, setAppointmentModalLoading] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<AppointmentDetailModal | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const mainAdmin = isMainAdmin();

  const authHeaders = () => {
    const token = localStorage.getItem('admin_token');
    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  };

  const fetchComplaint = async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${getApiBaseUrl()}/admin/complaints/${id}`, {
        headers: authHeaders(),
      });
      if (res.status === 401) {
        navigate('/admin/login');
        return;
      }
      if (!res.ok) throw new Error('شکایت یافت نشد');
      const data = await res.json();
      const normalizedStatus = normalizeComplaintStatus(data.status);
      const summary = (data.resolution_summary || '').trim();
      const finalized = summary.length > 0;
      const statusToShow = finalized ? 'closed' : normalizedStatus;
      setComplaint({ ...data, status: statusToShow });
      setStatus(statusToShow);
      setResolutionSummary(summary);
      setResolutionLocked(finalized);

      try {
        const relRes = await fetch(
          `${getApiBaseUrl()}/admin/complaints/${id}/related-appointments`,
          { headers: authHeaders() }
        );
        if (relRes.ok) {
          setRelatedAppointments(await relRes.json());
        } else {
          setRelatedAppointments([]);
        }
      } catch {
        setRelatedAppointments([]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'خطا');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!canAccessComplaints()) {
      navigate('/admin/dashboard', { replace: true });
      return;
    }
    fetchComplaint();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, navigate]);

  const aptStatusLabel = (s: string) => {
    switch (s) {
      case 'pending':
        return 'در انتظار';
      case 'in_progress':
        return 'در حال انجام';
      case 'completed':
        return 'تکمیل شده';
      case 'cancelled':
        return 'لغو شده';
      default:
        return s;
    }
  };

  const aptStatusColor = (s: string) => {
    switch (s) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const openAppointmentModal = async (appointmentId: number) => {
    setAppointmentModalOpen(true);
    setAppointmentModalLoading(true);
    setSelectedAppointment(null);
    setError(null);
    try {
      const res = await fetch(`${getApiBaseUrl()}/admin/appointments/${appointmentId}`, {
        headers: authHeaders(),
      });
      if (res.status === 401) {
        navigate('/admin/login');
        return;
      }
      if (!res.ok) throw new Error('خطا در دریافت جزئیات نوبت');
      setSelectedAppointment(await res.json());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'خطا');
      setAppointmentModalOpen(false);
    } finally {
      setAppointmentModalLoading(false);
    }
  };

  const closeAppointmentModal = () => {
    setAppointmentModalOpen(false);
    setSelectedAppointment(null);
  };

  const serviceNames = (apt: AppointmentDetailModal) => {
    if (apt.services?.length) {
      return apt.services.map((s) => s.name).join('، ');
    }
    return apt.service?.name || 'نامشخص';
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    const summaryText = resolutionSummary.trim();
    const savingFinalResult = !resolutionLocked && summaryText.length > 0;
    try {
      const res = await fetch(`${getApiBaseUrl()}/admin/complaints/${id}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({
          status: savingFinalResult ? 'closed' : status,
          ...(resolutionLocked
            ? {}
            : { resolution_summary: summaryText || null }),
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'خطا در ذخیره');
      }
      const data = await res.json();
      const summary = (data.resolution_summary || '').trim();
      const finalized = summary.length > 0;
      const statusToShow = finalized ? 'closed' : normalizeComplaintStatus(data.status);
      setComplaint({ ...data, status: statusToShow });
      setStatus(statusToShow);
      setResolutionSummary(summary);
      if (finalized) {
        setResolutionLocked(true);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'خطا');
    } finally {
      setSaving(false);
    }
  };

  const handleAddNote = async () => {
    const text = newNote.trim();
    if (!text) return;

    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`${getApiBaseUrl()}/admin/complaints/${id}/notes`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ notes: text }),
      });
      if (!res.ok) throw new Error('خطا در ثبت یادداشت');
      setNewNote('');
      await fetchComplaint();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'خطا');
    } finally {
      setSaving(false);
    }
  };

  const copyPhone = () => {
    if (complaint?.phone) {
      void navigator.clipboard.writeText(complaint.phone);
    }
  };

  const confirmDelete = async () => {
    if (deleting || !id) return;
    setDeleting(true);
    setError(null);
    try {
      const res = await fetch(`${getApiBaseUrl()}/admin/complaints/${id}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (res.status === 401) {
        navigate('/admin/login');
        return;
      }
      if (!res.ok) {
        let msg = 'حذف انجام نشد';
        try {
          const errBody = await res.json();
          if (errBody.detail) {
            msg = typeof errBody.detail === 'string' ? errBody.detail : JSON.stringify(errBody.detail);
          }
        } catch {
          /* ignore */
        }
        throw new Error(msg);
      }
      setShowDeleteModal(false);
      navigate('/admin/complaints');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'خطا در حذف');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner />
      </div>
    );
  }

  if (!complaint) {
    return (
      <div className="text-center py-16">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gray-100 mb-4">
          <svg className="w-7 h-7 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01" />
          </svg>
        </div>
        <p className="text-gray-600 font-medium">{error || 'شکایت یافت نشد'}</p>
        <button
          type="button"
          onClick={() => navigate('/admin/complaints')}
          className="mt-6 px-5 py-2.5 bg-blue-600 text-white rounded-xl text-sm hover:bg-blue-700 transition-colors"
        >
          بازگشت به لیست
        </button>
      </div>
    );
  }

  const isSurvey = complaint.submission_type === 'survey';
  const isFinalized = resolutionLocked;
  const displayStatus = status;
  const statusStyle = STATUS_STYLES[displayStatus];
  const headerStatusStyle = HEADER_STATUS_STYLES[displayStatus];
  const createdLabel = new Date(complaint.created_at).toLocaleString('fa-IR', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });

  return (
    <div className="max-w-5xl mx-auto space-y-6 pb-8">
      <ConfirmModal
        isOpen={showDeleteModal}
        title={isSurvey ? 'حذف دائمی نظرسنجی' : 'حذف دائمی شکایت'}
        message="این مورد از دیتابیس به‌طور کامل پاک می‌شود و قابل بازگشت نیست. آیا مطمئن هستید؟"
        onConfirm={() => void confirmDelete()}
        onCancel={() => {
          if (!deleting) setShowDeleteModal(false);
        }}
        confirmText={deleting ? 'در حال حذف...' : 'حذف کامل'}
        cancelText="انصراف"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
      />

      {/* Header */}
      <div className="relative overflow-hidden rounded-2xl border border-slate-700/60 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white shadow-xl">
        <div className="absolute -top-20 -right-20 w-56 h-56 rounded-full bg-amber-500/15 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 -left-16 w-48 h-48 rounded-full bg-blue-500/10 blur-3xl pointer-events-none" />
        <div className="absolute inset-0 bg-[linear-gradient(135deg,rgba(255,255,255,0.04)_0%,transparent_50%)] pointer-events-none" />

        <div className="relative px-5 py-4 sm:px-8 sm:py-6">
          <div className="flex items-center justify-between gap-3 mb-5">
            <button
              type="button"
              onClick={() => navigate('/admin/complaints')}
              className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-white transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              بازگشت به لیست
            </button>
            <div className="flex items-center gap-2">
              {mainAdmin && (
                <button
                  type="button"
                  onClick={() => setShowDeleteModal(true)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-red-200 border border-red-400/40 bg-red-500/10 hover:bg-red-500/20 transition-colors"
                >
                  حذف دائمی
                </button>
              )}
              <span className="inline-flex items-center gap-2">
              <span
                className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${
                  isSurvey ? 'bg-emerald-500/20 text-emerald-100 border border-emerald-400/40' : 'bg-amber-500/20 text-amber-100 border border-amber-400/40'
                }`}
              >
                {isSurvey ? 'نظرسنجی' : 'شکایت'}
              </span>
              {!isSurvey && (
                <span
                  className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${headerStatusStyle.badge}`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${headerStatusStyle.dot}`} />
                  {getStatusLabel(displayStatus)}
                </span>
              )}
            </span>
            </div>
          </div>

          <div className="flex items-center gap-4 min-w-0">
            <div className="shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center text-slate-900 font-bold text-xl sm:text-2xl shadow-lg shadow-amber-900/30">
              {complaint.full_name.charAt(0)}
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="text-lg sm:text-xl font-bold text-white truncate leading-tight">
                {complaint.full_name}
              </h1>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-sm text-slate-400">
                <span className="inline-flex items-center gap-1.5">
                  <span className="font-mono text-slate-200" dir="ltr">
                    {complaint.phone}
                  </span>
                  <button
                    type="button"
                    onClick={copyPhone}
                    title="کپی شماره"
                    className="p-1 rounded-md text-slate-500 hover:text-white hover:bg-white/10 transition-colors"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button>
                </span>
                <span className="hidden sm:inline text-slate-600" aria-hidden>
                  ·
                </span>
                <span className="font-mono text-slate-300" dir="ltr">
                  {complaint.tracking_code || `#${complaint.id}`}
                </span>
                <span className="hidden sm:inline text-slate-600" aria-hidden>
                  ·
                </span>
                <span className="text-slate-500">{createdLabel}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="flex items-start gap-3 p-4 rounded-xl bg-red-50 border border-red-100 text-red-800 text-sm">
          <svg className="w-5 h-5 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {error}
        </div>
      )}
      <div className="grid lg:grid-cols-5 gap-6">
        {/* Left column — customer / نظرسنجی */}
        <div className={`${isSurvey ? 'lg:col-span-5' : 'lg:col-span-2'} space-y-6`}>
          {relatedAppointments.length > 0 && (
          <section className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 bg-gray-50/80">
              <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-violet-100 text-violet-600">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </span>
                نوبت‌های قبلی (رزرو آنلاین)
              </h2>
            </div>
            <div className="p-5">
                  <ul className="space-y-2 max-h-48 overflow-y-auto">
                      {relatedAppointments.map((apt) => (
                        <li key={apt.id}>
                          <button
                            type="button"
                            onClick={() => openAppointmentModal(apt.id)}
                            className="w-full text-right p-2.5 rounded-lg border border-gray-100 hover:border-blue-200 hover:bg-blue-50/50 transition-colors"
                          >
                            <div className="flex justify-between gap-2 text-sm">
                              <span className="font-mono text-blue-700" dir="ltr">
                                {apt.tracking_code || `#${apt.id}`}
                              </span>
                              <span className="text-gray-500 text-xs">
                                {aptStatusLabel(apt.status)}
                              </span>
                            </div>
                            {apt.service_name && (
                              <p className="text-xs text-gray-600 mt-1 truncate">{apt.service_name}</p>
                            )}
                            <p className="text-xs text-gray-400 mt-0.5">
                              {new Date(apt.created_at).toLocaleDateString('fa-IR')}
                            </p>
                          </button>
                        </li>
                      ))}
                  </ul>
            </div>
          </section>
          )}

          {isSurvey && complaint.survey_data ? (
            <section className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100 bg-gray-50/80">
                <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                  <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-emerald-100 text-emerald-600">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                  </span>
                  پاسخ‌های نظرسنجی
                </h2>
              </div>
              <div className="p-5 space-y-3">
                <SurveyAnswerRow
                  label={SURVEY_QUESTION_LABELS.service_type}
                  value={complaint.survey_data.service_type}
                />
                <SurveyAnswerRow
                  label={SURVEY_QUESTION_LABELS.visit_date}
                  value={complaint.survey_data.visit_date}
                />
                {SURVEY_QUESTIONS.map((q) => (
                  <SurveyAnswerRow
                    key={q.id}
                    label={q.title}
                    value={complaint.survey_data?.[q.id]}
                  />
                ))}
              </div>
            </section>
          ) : (
            <section className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100 bg-gray-50/80">
                <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                  <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-amber-100 text-amber-600">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                    </svg>
                  </span>
                  متن شکایت
                </h2>
              </div>
              <div className="p-5">
                <blockquote className="text-gray-800 leading-relaxed whitespace-pre-wrap border-r-4 border-amber-400 pr-4 py-1">
                  {complaint.description}
                </blockquote>
              </div>
            </section>
          )}
        </div>

        {/* Right column — workflow (شکایت) */}
        {!isSurvey && (
        <div className="lg:col-span-3 space-y-6">
          <section className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 bg-gray-50/80 flex items-center justify-between">
              <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-violet-100 text-violet-600">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </span>
                یادداشت‌های پیگیری
              </h2>
              {complaint.notes.length > 0 && (
                <span className="text-xs font-medium text-gray-500 bg-gray-200/80 px-2.5 py-1 rounded-full">
                  {complaint.notes.length} مورد
                </span>
              )}
            </div>

            <div className="p-5">
              {complaint.notes.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <svg className="w-10 h-10 mx-auto mb-2 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  <p className="text-sm">هنوز یادداشتی ثبت نشده</p>
                </div>
              ) : (
                <ol className="relative border-r-2 border-gray-100 mr-3 space-y-6 mb-6">
                  {complaint.notes.map((note) => (
                    <li key={note.id} className="mr-6 relative">
                      <span className="absolute -right-[1.65rem] top-1.5 w-3 h-3 rounded-full bg-violet-400 ring-4 ring-white" />
                      <div className="rounded-xl border border-gray-100 bg-gray-50/50 p-4 hover:bg-gray-50 transition-colors">
                        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                          <span className="text-sm font-medium text-violet-700">{note.admin_username}</span>
                          <time className="text-xs text-gray-400">
                            {new Date(note.created_at).toLocaleString('fa-IR', {
                              dateStyle: 'short',
                              timeStyle: 'short',
                            })}
                          </time>
                        </div>
                        <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{note.notes}</p>
                      </div>
                    </li>
                  ))}
                </ol>
              )}

              {!isFinalized && (
                <div className="rounded-xl border-2 border-dashed border-gray-200 p-4 bg-gray-50/30">
                  <label className="block text-sm font-medium text-gray-700 mb-2">یادداشت جدید</label>
                  <textarea
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl bg-white focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 outline-none resize-none text-sm mb-3"
                    placeholder="تماس تلفنی، بررسی پرونده، پیگیری با واحد مربوطه..."
                  />
                  <button
                    type="button"
                    onClick={handleAddNote}
                    disabled={saving || !newNote.trim()}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-slate-800 text-white rounded-xl text-sm font-medium hover:bg-slate-900 disabled:opacity-50 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    افزودن یادداشت
                  </button>
                </div>
              )}
            </div>
          </section>

          <section className={`bg-white rounded-2xl shadow-sm border overflow-hidden ring-1 ${statusStyle.ring} border-gray-100`}>
            <div className="px-5 py-4 border-b border-gray-100">
              <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-indigo-100 text-indigo-600">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </span>
                رسیدگی و نتیجه نهایی
              </h2>
            </div>
            <div className="p-5 space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">وضعیت پرونده</label>
                {isFinalized ? (
                  <span
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border ${statusStyle.badge}`}
                  >
                    <span className={`w-2 h-2 rounded-full ${statusStyle.dot}`} />
                    {getStatusLabel(displayStatus)}
                  </span>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {STATUS_OPTIONS.map((opt) => {
                      const active = status === opt.value;
                      const style = STATUS_STYLES[opt.value];
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => setStatus(opt.value)}
                          className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border transition-all ${
                            active
                              ? `${style.badge} shadow-sm scale-[1.02]`
                              : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          <span className={`w-2 h-2 rounded-full ${active ? style.dot : 'bg-gray-300'}`} />
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  نتیجه نهایی را بنویسید
                </label>
                {resolutionLocked ? (
                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                    <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">
                      {resolutionSummary}
                    </p>
                    <p className="mt-3 text-xs text-slate-500 flex items-center gap-1.5">
                      <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                      نتیجه نهایی ثبت شده و قابل ویرایش نیست
                    </p>
                  </div>
                ) : (
                  <textarea
                    value={resolutionSummary}
                    onChange={(e) => setResolutionSummary(e.target.value)}
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none resize-none text-sm leading-relaxed"
                    placeholder="نتیجه رسیدگی و اقدام انجام‌شده را بنویسید..."
                  />
                )}
              </div>

              {!isFinalized && (
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 disabled:opacity-60 transition-colors shadow-sm shadow-blue-600/20"
                >
                  {saving ? (
                    <>
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      در حال ذخیره...
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {resolutionSummary.trim() ? 'ثبت نتیجه نهایی' : 'ذخیره وضعیت'}
                    </>
                  )}
                </button>
              )}
            </div>
          </section>
        </div>
        )}
      </div>

      {appointmentModalOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-50 overflow-y-auto flex items-center justify-center p-4"
          onClick={closeAppointmentModal}
          role="presentation"
        >
          <div
            className="relative w-full max-w-2xl bg-white rounded-2xl shadow-xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="appointment-modal-title"
          >
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
              <h3 id="appointment-modal-title" className="text-lg font-bold text-gray-900">
                {appointmentModalLoading
                  ? 'جزئیات نوبت'
                  : `جزئیات نوبت ${selectedAppointment?.tracking_code || ''}`}
              </h3>
              <button
                type="button"
                onClick={closeAppointmentModal}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="بستن"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6">
              {appointmentModalLoading ? (
                <div className="flex justify-center py-12">
                  <LoadingSpinner />
                </div>
              ) : selectedAppointment ? (
                <div className="space-y-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${aptStatusColor(
                        selectedAppointment.status
                      )}`}
                    >
                      {aptStatusLabel(selectedAppointment.status)}
                    </span>
                    <span className="text-sm text-gray-500 font-mono" dir="ltr">
                      #{selectedAppointment.tracking_code || selectedAppointment.id}
                    </span>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">نام</label>
                      <p className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded-lg">
                        {selectedAppointment.user?.full_name || complaint?.full_name || '-'}
                      </p>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">موبایل</label>
                      <p className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded-lg font-mono" dir="ltr">
                        {selectedAppointment.user?.phone || complaint?.phone || '-'}
                      </p>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">خدمت</label>
                      <p className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded-lg">
                        {serviceNames(selectedAppointment)}
                      </p>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">تاریخ مراجعه</label>
                      <p className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded-lg">
                        {selectedAppointment.appointment_date || '-'}
                        {selectedAppointment.appointment_time
                          ? ` — ${selectedAppointment.appointment_time}`
                          : ''}
                      </p>
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-xs font-medium text-gray-500 mb-1">تاریخ ثبت نوبت</label>
                      <p className="text-sm text-gray-900 bg-gray-50 px-3 py-2 rounded-lg">
                        {new Date(selectedAppointment.created_at).toLocaleString('fa-IR')}
                      </p>
                    </div>
                    {selectedAppointment.cancel_reason && (
                      <div className="sm:col-span-2">
                        <label className="block text-xs font-medium text-gray-500 mb-1">دلیل لغو</label>
                        <p className="text-sm text-red-800 bg-red-50 px-3 py-2 rounded-lg">
                          {selectedAppointment.cancel_reason}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              ) : null}
            </div>

            {selectedAppointment && !appointmentModalLoading && (
              <div className="sticky bottom-0 bg-gray-50 border-t border-gray-100 px-6 py-4 flex flex-wrap gap-3 justify-end rounded-b-2xl">
                <button
                  type="button"
                  onClick={closeAppointmentModal}
                  className="px-4 py-2.5 bg-white border border-gray-200 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50 transition-colors"
                >
                  بستن
                </button>
                <button
                  type="button"
                  onClick={() => {
                    closeAppointmentModal();
                    navigate(`/admin/appointments/${selectedAppointment.id}`);
                  }}
                  className="px-4 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 transition-colors"
                >
                  صفحهٔ کامل نوبت
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const SurveyAnswerRow: React.FC<{ label: string; value?: string }> = ({ label, value }) => (
  <div className="rounded-xl border border-gray-100 bg-gray-50/50 px-4 py-3">
    <p className="text-xs text-gray-500 mb-1">{label}</p>
    <p className="text-sm font-medium text-gray-900">{value || '—'}</p>
  </div>
);

export default AdminComplaintDetail;
